import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import MermaidDiagram from './MermaidDiagram';

function rawText(children: React.ReactNode): string {
    if (typeof children === 'string') return children;
    if (typeof children === 'number') return String(children);
    if (Array.isArray(children)) return children.map(rawText).join('');
    if (React.isValidElement(children)) {
        const props = children.props as { children?: React.ReactNode };
        return rawText(props?.children);
    }
    return '';
}

function preIsMermaid(node: unknown): boolean {
    // `node` is the HAST node react-markdown passes to component overrides.
    // For a fenced code block, the `<pre>` has a single `<code>` child whose
    // `properties.className` includes the `language-*` hint.
    const n = node as
        | { children?: Array<{ properties?: { className?: string | string[] } }> }
        | undefined;
    const code = n?.children?.[0];
    const cls = code?.properties?.className;
    if (Array.isArray(cls)) return cls.includes('language-mermaid');
    if (typeof cls === 'string') return cls.split(/\s+/).includes('language-mermaid');
    return false;
}

interface PostMarkdownProps {
    markdown: string;
}

const PostMarkdown: React.FC<PostMarkdownProps> = ({ markdown }) => (
    <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        components={{
            h1: ({ node, ...props }) => (
                <h1
                    className="font-display text-3xl sm:text-4xl text-ink-900 dark:text-ink-50 mt-12 mb-4 tracking-tight"
                    {...props}
                />
            ),
            h2: ({ node, ...props }) => (
                <h2
                    className="font-display text-2xl sm:text-3xl text-ink-900 dark:text-ink-50 mt-10 mb-3 tracking-tight"
                    {...props}
                />
            ),
            h3: ({ node, ...props }) => (
                <h3
                    className="font-mono text-xs uppercase tracking-[0.22em] text-accent dark:text-accent-soft mt-8 mb-2"
                    {...props}
                />
            ),
            p: ({ node, ...props }) => (
                <p
                    className="text-ink-800 dark:text-ink-100 leading-relaxed text-[15px] my-4 text-justify [hyphens:auto]"
                    {...props}
                />
            ),
            a: ({ node, href, ...props }) => (
                <a
                    href={href}
                    target={href && /^https?:/.test(href) ? '_blank' : undefined}
                    rel="noreferrer"
                    className="text-accent dark:text-accent-soft no-underline hover:underline decoration-accent/50 underline-offset-2"
                    {...props}
                />
            ),
            strong: ({ node, ...props }) => (
                <strong className="text-ink-900 dark:text-ink-50 font-medium" {...props} />
            ),
            em: ({ node, ...props }) => <em className="text-ink-800 dark:text-ink-100 italic" {...props} />,
            ul: ({ node, ...props }) => <ul className="list-disc pl-6 my-4 space-y-1" {...props} />,
            ol: ({ node, ...props }) => <ol className="list-decimal pl-6 my-4 space-y-1" {...props} />,
            li: ({ node, ...props }) => (
                <li className="text-ink-800 dark:text-ink-100 text-[15px] leading-relaxed" {...props} />
            ),
            code: ({ node, inline, className, children, ...props }: any) =>
                inline ? (
                    <code
                        className="font-mono text-[13px] bg-paper-200 dark:bg-ink-800 px-1.5 py-0.5 rounded"
                        {...props}
                    >
                        {children}
                    </code>
                ) : (
                    <code className="font-mono text-[13px] text-ink-800 dark:text-ink-100 block" {...props}>
                        {children}
                    </code>
                ),
            pre: ({ node, children, ...props }) => {
                if (preIsMermaid(node)) {
                    return <MermaidDiagram chart={rawText(children).trim()} />;
                }
                return (
                    <pre
                        className="bg-paper-100 dark:bg-night-800 border border-ink-200 dark:border-ink-700 rounded-md p-4 my-5 overflow-x-auto text-[13px] leading-relaxed"
                        {...props}
                    >
                        {children}
                    </pre>
                );
            },
            blockquote: ({ node, ...props }) => (
                <blockquote
                    className="border-l-2 border-accent dark:border-accent-soft pl-4 my-5 text-ink-700 dark:text-ink-200 italic"
                    {...props}
                />
            ),
            hr: () => <hr className="my-10 border-t border-ink-200 dark:border-ink-700" />,
        }}
    >
        {markdown}
    </ReactMarkdown>
);

export default PostMarkdown;
